// src/components/lineage/ThinkerProfileDrawer.jsx
// A lineage node opened as a person. Bottom sheet on mobile, centred panel on
// larger screens; the timeline stays behind it, so closing never costs the
// reader their place. Five beats, rendered only when authored — a thinker with
// no profile yet simply never offers the affordance that opens this.
import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, ArrowRight, BookOpen, CheckCircle } from 'lucide-react';
import { TRADITION_STYLES } from './traditionStyles';
import { TRADITIONS } from '../../data/lineageMap';
import useDialogA11y from '../../hooks/useDialogA11y';

// Work titles are authored as *Title* — the only markup profiles (and the
// lineage cut-through sentences) use. Shared with the timeline cards.
export const emphasise = (text) =>
  String(text).split(/\*([^*]+)\*/g).map((part, i) =>
    i % 2 === 1 ? <em key={i} className="italic">{part}</em> : part
  );

const BEATS = [
  { key: 'life', heading: 'The life' },
  { key: 'work', heading: 'The work' },
  { key: 'cutThrough', heading: 'Why they cut through' },
  { key: 'whyWeDrewOnThem', heading: 'Why we drew on them' }
];

const ThinkerProfileDrawer = ({ node, profile, met, module }) => {
  const navigate = useNavigate();

  // Back to wherever the reader came from — the timeline, or the module page
  // that offered "Meet them". A cold deep link has no history to return to.
  const close = useCallback(() => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate('/lineage', { replace: true });
  }, [navigate]);

  // Focus trap, initial focus, Escape and focus restore — the runtime audit
  // showed Tab walking straight out of the open drawer into the page behind.
  const dialogRef = useDialogA11y(!!node, close);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  if (!node) return null;
  const style = TRADITION_STYLES[node.tradition] || TRADITION_STYLES.modern;

  return (
    // z-[60]: above the floating Ask Sofia button (z-50), which otherwise
    // sits over the profile text on mobile while the reader is mid-beat.
    <div className="fixed inset-0 z-[60] sm:flex sm:items-center sm:justify-center sm:p-6">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${node.name}: profile`}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className={`absolute bottom-0 inset-x-0 max-h-[88vh] rounded-t-2xl sm:relative sm:inset-auto sm:max-h-[85vh] sm:w-full sm:max-w-2xl sm:rounded-2xl overflow-y-auto overscroll-contain bg-slate-900 border ${style.border} shadow-2xl`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-white/10 px-5 sm:px-8 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`}></span>
                <h2 className="text-xl sm:text-2xl font-bold text-white">{node.name}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full ${style.chip} ${style.text}`}>
                  {TRADITIONS[node.tradition]?.label}
                </span>
              </div>
              <p className="text-xs text-slate-300 mt-1">{node.dates}</p>
            </div>
            <button
              onClick={close}
              aria-label="Close profile"
              className="p-2 -m-2 text-slate-400 hover:text-white transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {module && (
            <p className="text-xs mt-2 flex items-center gap-1.5">
              {met ? (
                <>
                  <CheckCircle className="w-3.5 h-3.5 text-conversation-teal" />
                  <span className="text-conversation-teal">Met in Module {node.module}: {module.title}</span>
                </>
              ) : (
                <span className="text-slate-400">You'll meet them properly in Module {node.module}: {module.title}</span>
              )}
            </p>
          )}
        </div>

        {/* Beats */}
        <div className="px-5 sm:px-8 py-6 space-y-6">
          {BEATS.slice(0, 3).map(({ key, heading }) =>
            profile?.[key] ? (
              <section key={key}>
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${style.text} mb-2`}>{heading}</h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{emphasise(profile[key])}</p>
              </section>
            ) : null
          )}

          {/* The argument you met: back into the modules, at the section.
              A thinker the modules never quote directly (Marcus Aurelius)
              still gets the beat; only the section links need metIn. */}
          {(profile?.argument || (profile?.metIn && profile.metIn.length > 0)) && (
            <section>
              <h3 className={`text-xs font-semibold uppercase tracking-wider ${style.text} mb-2`}>The argument you met</h3>
              {profile.argument && (
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-3">{emphasise(profile.argument)}</p>
              )}
              <ul className="space-y-2">
                {(profile.metIn || []).map((ref, i) => (
                  <li key={i}>
                    <button
                      onClick={() => navigate(`/modules/${ref.moduleId}`, { state: { section: ref.section } })}
                      className="w-full text-left flex items-center gap-2 p-3 rounded-lg bg-slate-800/60 border border-slate-700 hover:border-slate-500 transition-colors group"
                    >
                      <BookOpen className={`w-4 h-4 flex-shrink-0 ${style.text}`} />
                      <span className="text-sm text-slate-300 group-hover:text-white transition-colors min-w-0">
                        <span className="text-slate-400">Module {ref.module} · </span>{ref.title}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-white ml-auto flex-shrink-0 transition-colors" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {BEATS.slice(3).map(({ key, heading }) =>
            profile?.[key] ? (
              <section key={key} className="pt-4 border-t border-white/10">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{heading}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{emphasise(profile[key])}</p>
              </section>
            ) : null
          )}

          {/* A node opened before its full profile exists still says something true */}
          {!profile && (
            <p className="text-sm text-slate-300 leading-relaxed">{node.oneLiner}</p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ThinkerProfileDrawer;
